import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  Button,
  message,
  Row,
  Col,
  Divider,
  Space,
  Spin,
  Alert
} from 'antd';
import { UserOutlined, MailOutlined, SaveOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { getUser, updateUser } from '../services/userService';
import './ProfilePage.css';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordMode, setPasswordMode] = useState(false);

  // Load user data on component mount
  useEffect(() => {
    if (!authLoading && user) {
      loadUserData();
    }
  }, [authLoading, user]);

  const loadUserData = async () => {
    setLoading(true);
    const result = await getUser(user.id);
    if (result.success) {
      setUserData(result.data);
      form.setFieldsValue({
        firstName: result.data.firstName,
        lastName: result.data.lastName,
        email: result.data.email
      });
    } else {
      message.error('Erreur lors du chargement des données');
    }
    setLoading(false);
  };

  const handleUpdateProfile = async (values) => {
    try {
      setLoading(true);

      // Préparer les données à mettre à jour
      const updateData = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email
      };

      // Si l'utilisateur change le mot de passe
      if (passwordMode && values.newPassword) {
        // Vérifier que l'ancien mot de passe est correct
        if (values.currentPassword !== userData.password) {
          message.error('Mot de passe actuel incorrect');
          setLoading(false);
          return;
        }
        updateData.password = values.newPassword;
      }

      // Appeler le service de mise à jour
      const result = await updateUser(user.id, updateData);

      if (result.success) {
        setUserData(result.data);
        message.success('Profil mis à jour avec succès');
        setPasswordMode(false);
        form.resetFields(['currentPassword', 'newPassword', 'confirmPassword']);
      } else {
        message.error(result.error?.message || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      message.error('Erreur lors de la mise à jour du profil');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || !user) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="profile-page">
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <h1>Mon Profil</h1>
        </Col>

        <Col xs={24} sm={24} md={16} lg={12} xl={10}>
          <Card loading={loading} className="profile-card">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleUpdateProfile}
              requiredMark={false}
            >
              {/* User Role Info */}
              <Alert
                message={`Rôle: ${user.role}`}
                type="info"
                showIcon
                style={{ marginBottom: '24px' }}
              />

              {/* Basic Information Section */}
              <div className="form-section">
                <h3>Informations Personnelles</h3>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="firstName"
                      label="Prénom"
                      rules={[{ required: true, message: 'Veuillez entrer votre prénom' }]}
                    >
                      <Input
                        prefix={<UserOutlined />}
                        placeholder="Votre prénom"
                        disabled={loading}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="lastName"
                      label="Nom"
                      rules={[{ required: true, message: 'Veuillez entrer votre nom' }]}
                    >
                      <Input
                        prefix={<UserOutlined />}
                        placeholder="Votre nom"
                        disabled={loading}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="email"
                  label="Email"
                  rules={[
                    { required: true, message: 'Veuillez entrer votre email' },
                    { type: 'email', message: 'Email invalide' }
                  ]}
                >
                  <Input
                    prefix={<MailOutlined />}
                    type="email"
                    placeholder="votre@email.com"
                    disabled={loading}
                  />
                </Form.Item>
              </div>

              <Divider />

              {/* Password Section */}
              <div className="form-section">
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ margin: 0 }}>Changer le Mot de Passe</h3>
                  <Button
                    type="link"
                    onClick={() => setPasswordMode(!passwordMode)}
                    style={{ marginLeft: 'auto' }}
                  >
                    {passwordMode ? 'Annuler' : 'Modifier'}
                  </Button>
                </div>

                {passwordMode && (
                  <>
                    <Form.Item
                      name="currentPassword"
                      label="Mot de passe actuel"
                      rules={[
                        {
                          required: true,
                          message: 'Veuillez entrer votre mot de passe actuel'
                        }
                      ]}
                    >
                      <Input.Password
                        placeholder="Entrez votre mot de passe actuel"
                        disabled={loading}
                        suffix={
                          <span
                            onClick={() => setShowPassword(!showPassword)}
                            style={{ cursor: 'pointer', color: '#999' }}
                          >
                            {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                          </span>
                        }
                      />
                    </Form.Item>

                    <Form.Item
                      name="newPassword"
                      label="Nouveau mot de passe"
                      rules={[
                        {
                          required: true,
                          message: 'Veuillez entrer un nouveau mot de passe'
                        },
                        {
                          min: 6,
                          message: 'Le mot de passe doit contenir au moins 6 caractères'
                        }
                      ]}
                    >
                      <Input.Password
                        placeholder="Entrez un nouveau mot de passe"
                        disabled={loading}
                        suffix={
                          <span
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            style={{ cursor: 'pointer', color: '#999' }}
                          >
                            {showNewPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                          </span>
                        }
                      />
                    </Form.Item>

                    <Form.Item
                      name="confirmPassword"
                      label="Confirmer le mot de passe"
                      dependencies={['newPassword']}
                      rules={[
                        {
                          required: true,
                          message: 'Veuillez confirmer votre mot de passe'
                        },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue('newPassword') === value) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              new Error('Les mots de passe ne correspondent pas')
                            );
                          }
                        })
                      ]}
                    >
                      <Input.Password
                        placeholder="Confirmez le nouveau mot de passe"
                        disabled={loading}
                      />
                    </Form.Item>
                  </>
                )}
              </div>

              <Divider />

              {/* Additional Info */}
              <div className="profile-info">
                <div className="info-row">
                  <span className="info-label">ID Utilisateur:</span>
                  <span className="info-value">{user.id}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Compte créé:</span>
                  <span className="info-value">
                    {userData?.createdAt
                      ? new Date(userData.createdAt).toLocaleDateString('fr-FR', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit'
                        })
                      : 'N/A'}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Dernière connexion:</span>
                  <span className="info-value">
                    {userData?.lastLogin
                      ? new Date(userData.lastLogin).toLocaleDateString('fr-FR', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                      : 'Jamais'}
                  </span>
                </div>
              </div>

              <Form.Item style={{ marginTop: '24px', marginBottom: 0 }}>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SaveOutlined />}
                    size="large"
                    loading={loading}
                  >
                    Enregistrer les Modifications
                  </Button>
                  <Button onClick={() => navigate('/')} disabled={loading}>
                    Retour
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        {/* Security Tips */}
        <Col xs={24} sm={24} md={8} lg={12} xl={12}>
          <Card className="tips-card" title="Conseils de Sécurité">
            <ul className="tips-list">
              <li>Utilisez un mot de passe fort avec au moins 6 caractères</li>
              <li>Ne partagez jamais votre mot de passe avec quiconque</li>
              <li>Changez votre mot de passe régulièrement</li>
              <li>Utilisez une combinaison de lettres et chiffres</li>
              <li>Confirmez votre identité avant de changer vos paramètres importants</li>
            </ul>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ProfilePage;
